import type { ReactElement } from "react";
import { Route, Routes } from "react-router-dom";

import { ProtectedRoute } from "./auth/ProtectedRoute";
import { HOME_PATH, SELECT_ORG_PATH } from "./auth/redirectTarget";
import { RequireActiveOrg } from "./auth/RequireActiveOrg";
import { RequireModule } from "./features/modules/RequireModule";
import { AppLayout } from "./layout/AppLayout";
import { HomeRedirect } from "./layout/HomeRedirect";
import { routeModules } from "./layout/navigation";
import { AgentRolesPage } from "./pages/AgentRolesPage";
import { BranchNameTemplatePage } from "./pages/BranchNameTemplatePage";
import { HelloPage } from "./pages/HelloPage";
import { LoginPage } from "./pages/LoginPage";
import { QueuePage } from "./pages/QueuePage";
import { RegisterPage } from "./pages/RegisterPage";
import { RepositorySettingsPage } from "./pages/RepositorySettingsPage";
import { ReviewerChecklistPage } from "./pages/ReviewerChecklistPage";
import { SelectOrgPage } from "./pages/SelectOrgPage";
import { SelfImprovementPage } from "./pages/SelfImprovementPage";
import { SettingsConnectionsPage } from "./pages/SettingsConnectionsPage";
import { SettingsOrganizationPage } from "./pages/SettingsOrganizationPage";
import { SettingsRunnersPage } from "./pages/SettingsRunnersPage";
import { SettingsSessionsPage } from "./pages/SettingsSessionsPage";
import { SettingsTrustRulesPage } from "./pages/SettingsTrustRulesPage";
import { TaskDetailPage } from "./pages/TaskDetailPage";
import { ToolAllowlistPage } from "./pages/ToolAllowlistPage";
import { ToolBlacklistPage } from "./pages/ToolBlacklistPage";

// Корневой роутинг приложения.
//
// Логика-через-роуты, а не модалки: глубокие ссылки — первоклассная
// фича, всё должно быть линкуемо.
//
// Публичные роуты (без app-shell):
//   /                    — лэндинг (HelloPage с healthz-кнопкой).
//   /login, /register    — формы аутентификации.
//
// Защищённые (требуют JWT, иначе → /login) живут под общим
// layout-route'ом
// `<ProtectedRoute><RequireActiveOrg><AppLayout/></RequireActiveOrg></ProtectedRoute>`:
// один `<Outlet/>` рендерит конкретную страницу внутри
// AppShell (header + navbar + хлебные крошки), не дублируя шапку на
// каждой странице. SSE-подписка нотификаций (внутри ProtectedRoute)
// благодаря этому монтируется один раз на всю защищённую ветку.
// `<RequireActiveOrg>` — гейт активной организации (ADR-0038, фаза 2):
// org-less сессию отправляет на /select-org.
//   /select-org                  — выбор активной организации (фаза 2
//                                   ADR-0038; внутри ProtectedRoute, но
//                                   вне AppLayout — навбар бесполезен
//                                   без активной org).
//   /home                        — развилка: редирект в первый доступный
//                                   раздел по составу модулей.
//   /queue                       — дашборд очереди.
//   /tasks/:id                   — экран одной задачи.
//   /self-improvement            — предложения самообучения.
//   /settings/organization       — имя организации и бюджет повторов.
//   /settings/connections        — подключение Forgejo.
//   /settings/runners            — раннеры организации и их токены
//                                   (ADR-0073).
//   /settings/sessions           — свои сессии (remember-me, отзыв).
//   /settings/trust-rules        — trust rules CRUD.
//   /settings/agent-roles        — промпты ролей агентов.
//   /settings/reviewer-checklist — доп-чек-лист reviewer'а per-(org,repo).
//   /settings/tool-allowlist     — allow-list инструментов per-(org,role).
//   /settings/tool-blacklist     — blacklist инструментов per-(org,role).
//   /settings/branch-name-template — шаблон имени head-ветки (org + per-repo).
//
// Каждый модульный раздел обёрнут в `<RequireModule>`: пока состав
// включённых модулей грузится — ожидание, модуля нет в составе —
// «раздел недоступен» (ADR-0083). Модули маршрута берутся из
// `layout/navigation.ts` (`routeModules`) — там же, где меню. Разделы
// ядра без обёртки.
export function App(): ReactElement {
  return (
    <Routes>
      <Route path="/" element={<HelloPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path={SELECT_ORG_PATH}
        element={
          <ProtectedRoute>
            <SelectOrgPage />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <RequireActiveOrg>
              <AppLayout />
            </RequireActiveOrg>
          </ProtectedRoute>
        }
      >
        <Route path={HOME_PATH} element={<HomeRedirect />} />
        <Route
          path="/queue"
          element={
            <RequireModule anyOf={routeModules("/queue")}>
              <QueuePage />
            </RequireModule>
          }
        />
        <Route
          path="/tasks/:id"
          element={
            <RequireModule anyOf={routeModules("/tasks/:id")}>
              <TaskDetailPage />
            </RequireModule>
          }
        />
        <Route
          path="/self-improvement"
          element={
            <RequireModule anyOf={routeModules("/self-improvement")}>
              <SelfImprovementPage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/organization"
          element={<SettingsOrganizationPage />}
        />
        <Route
          path="/settings/connections"
          element={
            <RequireModule anyOf={routeModules("/settings/connections")}>
              <SettingsConnectionsPage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/runners"
          element={
            <RequireModule anyOf={routeModules("/settings/runners")}>
              <SettingsRunnersPage />
            </RequireModule>
          }
        />
        <Route path="/settings/sessions" element={<SettingsSessionsPage />} />
        <Route
          path="/settings/trust-rules"
          element={
            <RequireModule anyOf={routeModules("/settings/trust-rules")}>
              <SettingsTrustRulesPage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/agent-roles"
          element={
            <RequireModule anyOf={routeModules("/settings/agent-roles")}>
              <AgentRolesPage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/reviewer-checklist"
          element={
            <RequireModule anyOf={routeModules("/settings/reviewer-checklist")}>
              <ReviewerChecklistPage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/tool-allowlist"
          element={
            <RequireModule anyOf={routeModules("/settings/tool-allowlist")}>
              <ToolAllowlistPage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/tool-blacklist"
          element={
            <RequireModule anyOf={routeModules("/settings/tool-blacklist")}>
              <ToolBlacklistPage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/branch-name-template"
          element={
            <RequireModule
              anyOf={routeModules("/settings/branch-name-template")}
            >
              <BranchNameTemplatePage />
            </RequireModule>
          }
        />
        <Route
          path="/settings/repository-settings"
          element={
            <RequireModule
              anyOf={routeModules("/settings/repository-settings")}
            >
              <RepositorySettingsPage />
            </RequireModule>
          }
        />
      </Route>
    </Routes>
  );
}
